import React, { useCallback } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  ReactFlowProvider,
  Node,
  Edge,
  NodeProps,
  ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FiDatabase, FiCpu, FiMessageSquare, FiZap } from 'react-icons/fi';

interface LineageNodeData extends Record<string, unknown> {
  label: string;
  namespace?: string;
  nodeType?: string;
  isFocus?: boolean;
}

type LineageNode = Node<LineageNodeData>;
type LineageEdge = Edge;

interface NodeStyleEntry {
  base: string;
  focus: string;
}

const nodeStyles: Record<string, NodeStyleEntry> = {
  dataset: {
    base: 'bg-gray-50 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm',
    focus: 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/20',
  },
  job: {
    base: 'bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-300 dark:border-emerald-700 rounded-lg shadow-sm',
    focus: 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-400 dark:border-blue-500',
  },
  prompt: {
    base: 'bg-purple-50 dark:bg-purple-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-lg shadow-sm',
    focus: 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-400 dark:border-blue-500',
  },
  agent: {
    base: 'bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-700 rounded-lg shadow-sm',
    focus: 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-400 dark:border-blue-500',
  },
};

function DatasetNode({ data, selected }: NodeProps<LineageNode>) {
  const isFocus = data.isFocus === true;
  const style = nodeStyles.dataset;
  const className = [
    'px-4 py-3 flex items-center gap-3 min-w-[180px]',
    style.base,
    (isFocus || selected) ? style.focus : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !border-2 !bg-gray-400" />
      <FiDatabase className="w-5 h-5 text-gray-600 dark:text-gray-300 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dataset</div>
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={data.label}>
          {data.label}
        </div>
        {data.namespace && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{data.namespace}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !border-2 !bg-gray-400" />
    </div>
  );
}

function JobNode({ data, selected }: NodeProps<LineageNode>) {
  const isFocus = data.isFocus === true;
  const style = nodeStyles.job;
  const className = [
    'px-4 py-3 flex items-center gap-3 min-w-[180px]',
    style.base,
    (isFocus || selected) ? style.focus : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !border-2 !bg-emerald-500" />
      <FiCpu className="w-5 h-5 text-emerald-700 dark:text-emerald-300 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">Job</div>
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={data.label}>
          {data.label}
        </div>
        {data.namespace && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{data.namespace}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !border-2 !bg-emerald-500" />
    </div>
  );
}

function PromptNode({ data, selected }: NodeProps<LineageNode>) {
  const isFocus = data.isFocus === true;
  const style = nodeStyles.prompt;
  const className = [
    'px-4 py-3 flex items-center gap-3 min-w-[180px]',
    style.base,
    (isFocus || selected) ? style.focus : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !border-2 !bg-purple-500" />
      <FiMessageSquare className="w-5 h-5 text-purple-700 dark:text-purple-300 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs font-medium text-purple-700 dark:text-purple-400 uppercase tracking-wide">Prompt</div>
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={data.label}>
          {data.label}
        </div>
        {data.namespace && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{data.namespace}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !border-2 !bg-purple-500" />
    </div>
  );
}

function AgentNode({ data, selected }: NodeProps<LineageNode>) {
  const isFocus = data.isFocus === true;
  const style = nodeStyles.agent;
  const className = [
    'px-4 py-3 flex items-center gap-3 min-w-[180px]',
    style.base,
    (isFocus || selected) ? style.focus : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !border-2 !bg-amber-500" />
      <FiZap className="w-5 h-5 text-amber-700 dark:text-amber-300 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">Agent</div>
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={data.label}>
          {data.label}
        </div>
        {data.namespace && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{data.namespace}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !border-2 !bg-amber-500" />
    </div>
  );
}

const nodeTypes = {
  dataset: DatasetNode,
  job: JobNode,
  prompt: PromptNode,
  agent: AgentNode,
};

const defaultEdgeOptions: Partial<LineageEdge> = {
  type: 'smoothstep',
};

interface LineageGraphInnerProps {
  initialNodes: LineageNode[];
  initialEdges: LineageEdge[];
  onNodeClick?: (node: LineageNode) => void;
}

function LineageGraphInner({ initialNodes, initialEdges, onNodeClick }: LineageGraphInnerProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<LineageNode>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<LineageEdge>(initialEdges);

  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onInit = useCallback((reactFlowInstance: ReactFlowInstance<LineageNode, LineageEdge>) => {
    setTimeout(() => reactFlowInstance.fitView({ padding: 0.2, duration: 200 }), 100);
  }, []);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: LineageNode) => {
      if (onNodeClick) onNodeClick(node);
    },
    [onNodeClick]
  );

  return (
    <ReactFlow<LineageNode, LineageEdge>
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={defaultEdgeOptions}
      onInit={onInit}
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.1}
      maxZoom={2}
      defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      proOptions={{ hideAttribution: true }}
      className="bg-gray-100/50 dark:bg-gray-900/50"
    >
      <Controls className="!shadow-md" />
      <MiniMap
        className="!bg-gray-200 dark:!bg-gray-800"
        nodeColor={(n) => (n.type === 'job' ? '#10b981' : '#9ca3af')}
        maskColor="rgba(0,0,0,0.1)"
      />
      <Background gap={16} size={1} color="rgba(156,163,175,0.3)" />
    </ReactFlow>
  );
}

interface LineageGraphProps {
  nodes: LineageNode[];
  edges: LineageEdge[];
  onNodeClick?: (node: LineageNode) => void;
}

function LineageGraph({ nodes: initialNodes, edges: initialEdges, onNodeClick }: LineageGraphProps) {
  return (
    <ReactFlowProvider>
      <div className="w-full h-[500px]" role="img" aria-label="Data lineage graph">
        <LineageGraphInner
          initialNodes={initialNodes}
          initialEdges={initialEdges}
          onNodeClick={onNodeClick}
        />
      </div>
    </ReactFlowProvider>
  );
}

export default LineageGraph;
export type { LineageNode, LineageEdge, LineageNodeData };
